import { CopyMoveErrors } from "../../../src/taskpane/config/constants";
import type { IContentManagerMoveTo } from "../../../src/taskpane/redux/models/reduxModels";

/**
 * The state update to apply when a copy/move attempt fails while the user is still
 * in destination-picking mode.
 *
 * Replaces a blanket `resetContentManagerMoveTo()`, which had two problems:
 *
 * 1. **It discarded the queue.** The selection the user assembled — often across
 *    several libraries — was thrown away, so a failed attempt meant starting over
 *    rather than picking a different destination.
 * 2. **It could fail silently.** The error was taken straight from the API result,
 *    which is `undefined` when the failure is a client-side throw rather than an HTTP
 *    response. Every error indicator is gated on a TRUTHY error, so the operation
 *    disappeared with no message at all.
 *
 * What it keeps: mode, selectedFiles, workingDirectories, sourceSiteUrl,
 * sourceDisplayName, and `moveToEnabled: true` — everything needed to retry at
 * another destination or to Cancel deliberately.
 *
 * What it must clear, with LITERAL `false`/`[]`: every busy flag and the job list.
 * The reducer merges these by `!= undefined`, so `undefined` would preserve them —
 * leaving `busyFetchingFiles` set re-enters the fetch loop, and `busyMovingFiles`
 * leaves a spinner running forever against jobs that do not exist.
 *
 * NOT for the drag-and-drop move path: that starts a job immediately with no pending
 * queue to preserve, so a full reset remains correct there.
 */
/**
 * The user-facing text for a copy/move error.
 *
 * `CopyMoveErrors.*` are raw English sentinels, never display copy — every surface that
 * shows `moveTo.error` has to run it through here or it ships untranslated text. Shared
 * by the tray and the move-files panel so the two cannot describe the same failure
 * differently.
 *
 * Anything unrecognized is passed through: that is a message from SharePoint itself,
 * which is already human-readable and more specific than any generic fallback.
 */
export const resolveCopyMoveErrorMessage = (
    error: string | undefined,
    languageStrings: { MultipleFilesFoldersCopyMove: string; ErrorMessageGeneric: string }
): string => {
    if (!error) {
        return "";
    }

    switch (error) {
        // Different causes, same accurate sentence: something of that name is already
        // at that location.
        case CopyMoveErrors.DuplicateFilenames:
        case CopyMoveErrors.SameSourceAndDestination:
            return languageStrings.MultipleFilesFoldersCopyMove;
        case CopyMoveErrors.UnresolvableSourcePaths:
        case CopyMoveErrors.Unknown:
            return languageStrings.ErrorMessageGeneric;
        default:
            return error;
    }
};

export const buildCopyMoveFailureUpdate = (error?: string): IContentManagerMoveTo => ({
    // Keep the user in destination-picking mode with their queue intact.
    moveToEnabled: true,
    busyFetchingFiles: false,
    busyMovingFiles: false,
    isPreparingJobs: false,
    isMoveInProgress: false,
    isMoveComplete: false,
    copyJobResults: [],
    copyJobResultsCompletedIds: [],
    progressCountCompleted: 0,
    // Always truthy: an empty or missing message is what made failures invisible.
    error: error ? error : CopyMoveErrors.Unknown,
});
